import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ChevronDown, FileText, Plus } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const badge = (d) => d.locked ? { c: "bg-amber-100 text-amber-800", t: d.stale ? "Re-consent needed" : "Locked — awaiting consent" } : { c: "bg-teal text-white", t: "Active (consented)" };

export default function ChildrenPrivacy() {
  const [data, setData] = useState({ dancers: [], current_version: "" });
  const [notices, setNotices] = useState([]);
  const [audit, setAudit] = useState({});
  const [open, setOpen] = useState({});
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ title: "", text: "", material: false });

  const load = () => {
    api.get("/consent/status").then((r) => setData(r.data)).catch(() => {});
    api.get("/consent/notices").then((r) => setNotices(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const toggle = async (id) => {
    setOpen((o) => ({ ...o, [id]: !o[id] }));
    if (!audit[id]) {
      try { const r = await api.get(`/consent/audit/${id}`); setAudit((a) => ({ ...a, [id]: r.data })); } catch { /* ignore */ }
    }
  };
  const publish = async () => {
    if (!form.title.trim() || !form.text.trim()) { toast.error("Add a title and notice text"); return; }
    try {
      const r = await api.post("/consent/notices", form);
      toast.success(r.data.reconsent_required ? "Published — parents must re-consent" : "New notice version published");
      setDlg(false); setForm({ title: "", text: "", material: false }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="stagger-in">
      <PageHeader title="Children's Privacy" subtitle="Read-only consent status for dancers under 13, the audit trail, and the parental notice library." testid="children-privacy-page" />

      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" data-testid="notice-library">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-teal" />Parental Notice Library <span className="text-xs text-muted-foreground">current {data.current_version}</span></h3>
          <Button size="sm" className="rounded-full bg-teal hover:bg-teal-dark" data-testid="new-notice-button" onClick={() => setDlg(true)}><Plus className="w-4 h-4 mr-1" />New version</Button>
        </div>
        <div className="space-y-2">
          {notices.map((n) => (
            <div key={n.id} data-testid={`notice-${n.version}`} className="rounded-xl border p-3">
              <p className="text-sm font-semibold">{n.version} · {n.title} {n.material && <span className="text-[10px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 ml-1">material · required re-consent</span>}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.text}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()} · {n.created_by}</p>
            </div>
          ))}
        </div>
      </div>

      <h3 className="font-head font-bold mb-3">Under-13 dancers</h3>
      {(!data.dancers || data.dancers.length === 0) ? <EmptyState icon={ShieldCheck} title="No under-13 dancers" hint="No dancer records are under 13." testid="cp-empty" /> :
        <div className="space-y-3">
          {data.dancers.map((d) => { const b = badge(d); return (
            <div key={d.dancer_id} data-testid={`cp-row-${d.dancer_id}`} className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold">{d.name} <span className="text-xs text-muted-foreground">age {d.age}</span></p>
                  {d.consent?.date && <p className="text-xs text-muted-foreground">Consented {new Date(d.consent.date).toLocaleDateString()} by {d.consent.parent_name} · notice {d.consent.notice_version} · {d.consent.method}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${b.c}`}>{b.t}</span>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`cp-audit-${d.dancer_id}`} onClick={() => toggle(d.dancer_id)}>Audit <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${open[d.dancer_id] ? "rotate-180" : ""}`} /></Button>
                </div>
              </div>
              {open[d.dancer_id] && <div className="mt-3 border-t pt-2 space-y-1" data-testid={`cp-audit-list-${d.dancer_id}`}>
                {(audit[d.dancer_id] || []).length === 0 ? <p className="text-xs text-muted-foreground">No events yet.</p> :
                  (audit[d.dancer_id] || []).map((e) => (
                    <p key={e.id} className="text-xs"><span className="font-semibold">{e.action}</span> · {e.actor_role}{e.actor_name ? ` (${e.actor_name})` : ""} · {new Date(e.at).toLocaleString()}</p>
                  ))}
              </div>}
            </div>
          ); })}
        </div>}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg" data-testid="notice-dialog">
          <DialogHeader><DialogTitle className="font-head">Publish new notice version</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="notice-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notice text</Label><Textarea data-testid="notice-text" rows={6} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="notice-material" checked={form.material} onChange={(e) => setForm({ ...form, material: e.target.checked })} />This is a material change — require all parents to re-consent</label>
          </div>
          <DialogFooter><Button data-testid="publish-notice-button" className="rounded-full bg-teal hover:bg-teal-dark" onClick={publish}>Publish</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
